import { PopoverPlacement } from '@finviz/website/js/main/components/popover/types'
import classnames from 'classnames'
import React from 'react'

import {
  Button,
  ButtonComponentProps,
  Delayed,
  Popover,
  PopoverTrigger,
  Spinner,
  getExtendedDialogState,
  useElementMeasure,
  usePopoverState,
} from '../../components'
import { SpecificChartFunctionality } from '../../constants/common'
import {
  chartSettingsAsyncModule,
  preventClosingUnsavedChangesAsyncModule,
} from '../../controllers/chart-settings/async-modules'
import ChartLayout from '../../models/chart_layout'
import { useAsyncModule } from '../../utils/async-modules-utils'
import { encodeQueryString } from '../../utils/query_string'

const SETTINGS_WIDTH = 580 // w-145
const BUTTON_POPOVER_GUTTER = 16
const SETTINGS_POPOVER_ID = 'settings-popover'

const utmCampaign: Partial<Record<SpecificChartFunctionality, string>> = {
  [SpecificChartFunctionality.quotePage]: 'quote-settings-button',
  [SpecificChartFunctionality.futuresPage]: 'futures-settings-button',
  [SpecificChartFunctionality.forexPage]: 'forex-settings-button',
  [SpecificChartFunctionality.cryptoPage]: 'crypto-settings-button',
}

type PopoverStylesType = { body?: React.CSSProperties; wrapper?: React.CSSProperties }

export function ScrollableChartSettingsPopover({ state, model }: { state: any; model: ChartLayout }) {
  const { elementWidth: bodyWidth } = useElementMeasure(document.body)
  const [chartSettingsModule] = useAsyncModule({
    ...chartSettingsAsyncModule,
    shouldLoadModule: state.visible,
    errorSeverity: 'medium',
    onError: () => state.hide(),
  })
  const [preventClosingUnsavedChangesModule] = useAsyncModule({
    ...preventClosingUnsavedChangesAsyncModule,
    shouldLoadModule: state.visible,
    errorSeverity: 'medium',
    onError: () => state.hide(),
  })

  /**
   * The reason for this workaround is that the popover with a loader while fetching async modules remounts and resets
   * all animations when all async modules are loaded and the popover is rerendered inside PreventClosingUnsavedChanges
   * */
  const checkForUnsavedChangesAndCloseRef = React.useRef(() => {})

  const { ChartSettings } = chartSettingsModule ?? {}
  const { PreventClosingUnsavedChanges } = preventClosingUnsavedChangesModule ?? {}
  const { isFullyClosed } = getExtendedDialogState(state)
  const chartElement = document.getElementById('chart')
  const popoverContentElement = document.getElementById(SETTINGS_POPOVER_ID)
  const settingsButton: HTMLButtonElement = state.unstable_referenceRef.current

  const popoverStyles = React.useMemo<PopoverStylesType>(() => {
    const newPopoverStyles: PopoverStylesType = {}
    if (!settingsButton || !chartElement) {
      return newPopoverStyles
    }
    const buttonRect = settingsButton.getBoundingClientRect()
    const topOffset = buttonRect.top + buttonRect.height
    newPopoverStyles.body = {
      maxHeight: window.innerHeight - topOffset - BUTTON_POPOVER_GUTTER * 2,
      position: 'sticky',
      top: -topOffset + BUTTON_POPOVER_GUTTER - window.scrollY,
    }
    if (!popoverContentElement) {
      return newPopoverStyles
    }
    const chartHeight = chartElement.clientHeight
    const popoverContentHeight = popoverContentElement.offsetHeight
    const emptySpaceDiff = bodyWidth - model.width
    const maxOffset = SETTINGS_WIDTH - buttonRect.width

    let rightOffset = emptySpaceDiff > 150 ? (emptySpaceDiff / 2) * -1 : 0
    if (emptySpaceDiff / 2 > maxOffset) rightOffset = maxOffset * -1

    newPopoverStyles.wrapper = {
      height: popoverContentHeight < chartHeight ? chartHeight : undefined,
      marginRight: rightOffset,
    }

    return newPopoverStyles
  }, [settingsButton, chartElement, popoverContentElement, model.width, bodyWidth])

  const hasAsyncModules = !!(PreventClosingUnsavedChanges && ChartSettings)

  return (
    <Popover
      id={SETTINGS_POPOVER_ID}
      aria-label="Chart Settings"
      className={classnames('relative z-10 flex w-145 max-w-full flex-col', {
        'max-h-full': hasAsyncModules,
        'h-full': !hasAsyncModules,
      })}
      state={state}
      style={popoverStyles.body}
      popoverWrapperStyle={popoverStyles.wrapper}
      focusOnHide={false}
      hideOnClickOutside={false}
      onOverlayClick={checkForUnsavedChangesAndCloseRef.current}
    >
      {hasAsyncModules ? (
        <PreventClosingUnsavedChanges onHide={state.hide}>
          {({ checkForUnsavedChangesAndClose }) => {
            checkForUnsavedChangesAndCloseRef.current = checkForUnsavedChangesAndClose
            return !isFullyClosed ? <ChartSettings onClose={checkForUnsavedChangesAndClose} /> : null
          }}
        </PreventClosingUnsavedChanges>
      ) : (
        <Delayed>
          <div className="flex h-full w-full items-center justify-center">
            <Spinner width={24} />
          </div>
        </Delayed>
      )}
    </Popover>
  )
}

interface ChartSettingsButtonProps extends Pick<ButtonComponentProps, 'theme' | 'size' | 'rounding'> {
  isPremium: boolean
  placement?: PopoverPlacement
  model: ChartLayout
}

export function ChartSettingsButton({
  isPremium,
  model,
  placement,
  ...props
}: React.PropsWithChildren<ChartSettingsButtonProps>) {
  const settingsPopoverState = usePopoverState({ placement: placement ?? 'bottom-end' })

  const buttonProps = React.useMemo(() => {
    if (model.specificChartFunctionality !== SpecificChartFunctionality.quotePage && !isPremium) {
      return {
        as: 'a',
        href: `/elite.ashx?${encodeQueryString({
          utm_source: 'finviz',
          utm_medium: 'banner',
          utm_campaign: utmCampaign[model.specificChartFunctionality],
        })}`,
      } as ButtonComponentProps<'a'>
    }

    return {
      as: PopoverTrigger,
      state: settingsPopoverState,
      active: settingsPopoverState.visible,
    } as ButtonComponentProps<typeof PopoverTrigger>
  }, [model, isPremium, settingsPopoverState])

  return (
    <>
      <Button
        {...buttonProps}
        as={buttonProps.as}
        focusRing={false}
        data-testid="chart-settings-open-btn"
        appearance={props.children ? undefined : 'square'}
        title={props.children ? undefined : 'Chart Settings'}
        leftContent="settingsWheel"
        {...props}
      />
      <ScrollableChartSettingsPopover state={settingsPopoverState} model={model} />
    </>
  )
}
